import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, ArrowRight, Search } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function AgentBookShipment() {
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    origin_name: "", origin_address: "", origin_city: "", origin_pincode: "", origin_phone: "",
    dest_name: "", dest_address: "", dest_city: "", dest_pincode: "", dest_phone: "",
    weight: "", pkg_type: "parcel", description: "",
  });

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async (q = "") => {
    try {
      const params = { role: "customer" };
      if (q) params.search = q;
      const res = await api.get("/users", { params });
      setCustomers(res.data || []);
    } catch { /* ignore */ }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const calculatePrice = async () => {
    if (!form.weight || !form.origin_pincode || !form.dest_pincode) return;
    try {
      const res = await api.post("/pricing/calculate", {
        weight: parseFloat(form.weight),
        origin_pincode: form.origin_pincode,
        destination_pincode: form.dest_pincode,
      });
      setEstimatedPrice(res.data.price);
    } catch { setEstimatedPrice(null); }
  };

  const handleNext = async () => {
    if (step === 3) await calculatePrice();
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) { toast.error("Please select a customer."); return; }
    setLoading(true);
    try {
      const res = await api.post("/shipments", {
        customer_id: selectedCustomer.id,
        origin: { name: form.origin_name, address: form.origin_address, city: form.origin_city, pincode: form.origin_pincode, phone: form.origin_phone },
        destination: { name: form.dest_name, address: form.dest_address, city: form.dest_city, pincode: form.dest_pincode, phone: form.dest_phone },
        package: { weight: parseFloat(form.weight), type: form.pkg_type, description: form.description }
      });
      setResult(res.data);
      setStep(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed.");
    } finally {
      setLoading(false);
    }
  };

  const steps = ["Customer", "Sender", "Receiver", "Package", "Done"];

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Book Shipment</h1>
          <p className="text-slate-500 text-sm">Book a shipment on behalf of a customer.</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-1">
          {steps.map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? "bg-orange-500 text-white" : step === i + 1 ? "bg-orange-500 text-white ring-4 ring-orange-100" : "bg-slate-200 text-slate-500"}`}>
                  {step > i + 1 ? "✓" : i + 1}
                </div>
                <span className={`text-xs mt-1 hidden sm:block ${step === i + 1 ? "text-orange-500 font-semibold" : "text-slate-400"}`}>{label}</span>
              </div>
              {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${step > i + 1 ? "bg-orange-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          {/* Step 1: Select Customer */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading font-semibold text-lg text-slate-800 mb-4 uppercase">Select Customer</h2>
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); fetchCustomers(e.target.value); }}
                    placeholder="Search customers..." data-testid="customer-search-input"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-50">
                {customers.length === 0 ? (
                  <p className="text-center text-slate-400 text-sm py-6">No customers found.</p>
                ) : customers.map((c) => (
                  <button key={c.id} onClick={() => setSelectedCustomer(c)}
                    data-testid={`select-customer-${c.id}`}
                    className={`w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 transition-colors ${selectedCustomer?.id === c.id ? "bg-brand-50 border-l-2 border-brand-500" : ""}`}>
                    <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{c.name?.[0]}</div>
                    <div>
                      <p className="font-semibold text-sm text-slate-800">{c.name}</p>
                      <p className="text-xs text-slate-400">{c.email}</p>
                    </div>
                    {selectedCustomer?.id === c.id && <span className="ml-auto text-brand-500 text-xs font-bold">Selected</span>}
                  </button>
                ))}
              </div>
              {selectedCustomer && (
                <div className="mt-3 bg-brand-50 rounded-sm p-3 text-sm text-brand-700 font-medium">
                  Selected: {selectedCustomer.name} ({selectedCustomer.email})
                </div>
              )}
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading font-semibold text-lg text-slate-800 mb-4 uppercase">Sender Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { name: "origin_name", label: "Name", placeholder: "Sender's name" },
                  { name: "origin_phone", label: "Phone", placeholder: "+91 9876543210" },
                  { name: "origin_address", label: "Address", placeholder: "Street address", full: true },
                  { name: "origin_city", label: "City", placeholder: "Mumbai" },
                  { name: "origin_pincode", label: "PIN Code", placeholder: "400001" },
                ].map((f) => (
                  <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
                    <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} data-testid={`agent-${f.name}`}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading font-semibold text-lg text-slate-800 mb-4 uppercase">Receiver Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { name: "dest_name", label: "Name", placeholder: "Receiver's name" },
                  { name: "dest_phone", label: "Phone", placeholder: "+91 9876543210" },
                  { name: "dest_address", label: "Address", placeholder: "Street address", full: true },
                  { name: "dest_city", label: "City", placeholder: "Delhi" },
                  { name: "dest_pincode", label: "PIN Code", placeholder: "110001" },
                ].map((f) => (
                  <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
                    <input name={f.name} value={form[f.name]} onChange={handleChange} placeholder={f.placeholder} data-testid={`agent-${f.name}`}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading font-semibold text-lg text-slate-800 mb-4 uppercase">Package Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Weight (kg)</label>
                  <input type="number" name="weight" value={form.weight} onChange={handleChange} step="0.1" min="0.1" placeholder="1.5" data-testid="agent-weight"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Package Type</label>
                  <select name="pkg_type" value={form.pkg_type} onChange={handleChange} className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:border-brand-500">
                    <option value="parcel">Parcel</option>
                    <option value="document">Document</option>
                    <option value="fragile">Fragile</option>
                    <option value="electronics">Electronics</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label>
                  <textarea name="description" value={form.description} onChange={handleChange} rows={2} placeholder="Contents description..."
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none resize-none" />
                </div>
              </div>
              {estimatedPrice !== null && (
                <div className="mt-4 bg-orange-50 border border-orange-100 rounded-lg p-4 flex items-center justify-between">
                  <p className="text-sm font-semibold text-orange-700">Estimated Price</p>
                  <span className="font-heading text-2xl font-bold text-orange-500">₹{estimatedPrice}</span>
                </div>
              )}
            </motion.div>
          )}

          {step === 5 && result && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">Booking Confirmed!</h2>
              <p className="text-slate-500 text-sm mb-4">For: {selectedCustomer?.name}</p>
              <div className="bg-brand-50 rounded-lg p-5 inline-block">
                <p className="text-xs font-bold text-brand-600 uppercase mb-1">Tracking ID</p>
                <p className="font-heading text-3xl font-bold text-brand-500">{result.tracking_id}</p>
                <p className="text-sm text-slate-500 mt-1">₹{result.price}</p>
              </div>
              <div className="flex gap-3 justify-center mt-6">
                <button onClick={() => navigate("/agent/bookings")} className="px-5 py-2 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 uppercase">View Bookings</button>
                <button onClick={() => { setStep(1); setResult(null); setSelectedCustomer(null); setEstimatedPrice(null); setForm({ origin_name: "", origin_address: "", origin_city: "", origin_pincode: "", origin_phone: "", dest_name: "", dest_address: "", dest_city: "", dest_pincode: "", dest_phone: "", weight: "", pkg_type: "parcel", description: "" }); }}
                  className="px-5 py-2 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm uppercase">Book Another</button>
              </div>
            </motion.div>
          )}

          {step < 5 && (
            <div className="flex justify-between mt-6 pt-4 border-t border-slate-100">
              {step > 1 ? (
                <button onClick={() => setStep(step - 1)} className="px-5 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm hover:bg-slate-50">Back</button>
              ) : <div />}
              {step < 4 ? (
                <button onClick={handleNext} disabled={step === 1 && !selectedCustomer} data-testid={`agent-book-next-${step}`}
                  className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-sm hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50">
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={loading} data-testid="agent-book-confirm"
                  className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-sm hover:bg-orange-600 disabled:opacity-70">
                  {loading ? "Booking..." : "Confirm Booking"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
